"use client";

import clsx from "clsx";
import React, { forwardRef, useId, useState } from "react";
import { FieldLabel } from "~/components/Forms";
import { IoEye, IoEyeOff } from "react-icons/io5";

export const PasswordField = forwardRef<
    HTMLInputElement,
    Omit<React.ComponentPropsWithoutRef<"input">, "id"> & {
    label?: string;
    labelClassName?: string;
    inputClassName?: string;
    error?: string;
    note?: string;
    noteClassName?: string;
    light?: boolean;
}
>((
    { label, className, labelClassName, inputClassName, error, note, noteClassName, light, ...props },
    ref
) => {
    const id = useId();
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    return (
        <div className={className}>
            {label && (
                <FieldLabel id={id} className={labelClassName} required={!!props.required}>
                    {label}
                </FieldLabel>
            )}

            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsPasswordVisible((v) => !v)}
                    className={clsx(
                        "absolute inset-y-0 left-0 w-12 flex items-center justify-center rounded-l-md focus:outline-none",
                        error
                            ? "bg-rose-200 text-rose-400 hover:text-rose-600"
                            : (light ? "bg-zinc-200 text-zinc-500 hover:text-zinc-700" : "bg-zinc-100 text-zinc-300 hover:text-zinc-500")
                    )}
                    aria-label={isPasswordVisible ? "Hide" : "Show"}>
                    {isPasswordVisible ? <IoEyeOff size={24} /> : <IoEye size={24} />}
                </button>

                <input
                    ref={ref}
                    id={id}
                    aria-label="Password"
                    aria-invalid={!!error}
                    type={isPasswordVisible ? "text" : "password"}
                    className={clsx(
                        "field",                // centralized base input styles
                        error && "field-error", // centralized error skin
                        "pl-16 pr-3",           // space for the toggle button
                        inputClassName
                    )}
                    {...props}
                />
            </div>

            {note && <div className={clsx("field-note", noteClassName)}>{note}</div>}
            {error && <div className="field-error-text">{error}</div>}
        </div>
    );
});
